import {
  Box,
  Card,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Shield, ShieldCheck, Trash2, UserRound } from "lucide-react";
import {
  AccessPoint,
  INVITE_ROLES,
  PHONE_COUNTRY_CODES,
  unitNameForInviteRole,
  WizardUnit,
  WizardUser,
} from "../types";
import RequiredMark from "../components/RequiredMark";
import { passtrackColors } from "../theme";
import {
  getInviteEmailError,
  getInvitePhoneError,
  isValidInviteEmail,
  isValidPhoneDigits,
} from "../utils/userValidation";

const ROLE_ICONS: Record<string, typeof Shield> = {
  shield: Shield,
  "user-round": UserRound,
  "shield-check": ShieldCheck,
};

interface UsersStepProps {
  users: WizardUser[];
  onRemoveUser: (id: string) => void;
  newUser: WizardUser;
  onUpdateNewUser: (patch: Partial<WizardUser>) => void;
  onAddUser: () => void;
  units: WizardUnit[];
  accessPoints: AccessPoint[];
  hasAdminUser: boolean;
}

export default function UsersStep({
  users,
  onRemoveUser,
  newUser,
  onUpdateNewUser,
  onAddUser,
  units,
  accessPoints,
  hasAdminUser,
}: UsersStepProps) {
  const roleMeta =
    INVITE_ROLES.find((r) => r.key === newUser.role) ?? INVITE_ROLES[0];

  const emailError = getInviteEmailError(newUser.email);
  const phoneError = getInvitePhoneError(newUser.phoneNumber);
  const canAddUser =
    newUser.name.trim().length > 0 &&
    isValidInviteEmail(newUser.email) &&
    isValidPhoneDigits(newUser.phoneNumber) &&
    (!roleMeta.needsUnit || newUser.unit.trim().length > 0) &&
    (!roleMeta.needsPoints || newUser.points.length > 0);

  const initialsOf = (name: string) => {
    const parts = (name || "?").trim().split(/\s+/);
    return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 1180 }}>
      <Card variant="outlined" sx={{ p: 3.5, borderRadius: 2.5 }}>
        <Typography fontWeight={600} color={passtrackColors.ink}>
          Usuarios de la propiedad
        </Typography>
        <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted, mb: 2.5 }}>
          Para continuar debes dar de alta por lo menos un administrador. Puedes
          invitar más usuarios ahora o hacerlo después desde la plataforma.
        </Typography>

        <Box
          sx={{
            border: `1px solid ${passtrackColors.borderSubtle}`,
            borderRadius: 2,
            overflow: "hidden",
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "minmax(170px,2fr) minmax(150px,1.6fr) 150px 40px",
              gap: 1.5,
              px: 2,
              py: 1.5,
              background: passtrackColors.mist,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: passtrackColors.textMuted,
            }}
          >
            <span>Usuario</span>
            <span>Unidad / puntos</span>
            <span>Rol</span>
            <span />
          </Box>
          {users.length === 0 && (
            <Box sx={{ px: 2, py: 2.5 }}>
              <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted }}>
                Aún no hay usuarios invitados.
              </Typography>
            </Box>
          )}
          {users.map((u, i) => {
            const isAdmin = u.role === "Administración";
            const isGuard = u.role === "Guardia de Seguridad";
            const scope = isGuard
              ? u.points.length
                ? u.points
                    .map((id) => accessPoints.find((p) => p.id === id)?.nombre)
                    .filter(Boolean)
                    .join(", ")
                : "Sin puntos asignados"
              : u.unit || "—";
            return (
              <Box
                key={u.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(170px,2fr) minmax(150px,1.6fr) 150px 40px",
                  gap: 1.5,
                  alignItems: "center",
                  px: 2,
                  py: 1.75,
                  borderTop: i
                    ? `1px solid ${passtrackColors.borderSubtle}`
                    : "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      flex: "none",
                      borderRadius: 99,
                      background: isAdmin
                        ? passtrackColors.blue
                        : isGuard
                          ? passtrackColors.cloud
                          : passtrackColors.greenMist,
                      color: isAdmin
                        ? "#fff"
                        : isGuard
                          ? passtrackColors.blue900
                          : passtrackColors.green700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {initialsOf(u.name).toUpperCase()}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: passtrackColors.ink,
                      }}
                    >
                      {u.name}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{ fontSize: 12, color: passtrackColors.textMuted }}
                    >
                      {u.email || "Sin correo"}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  noWrap
                  sx={{ fontSize: 13, color: passtrackColors.graphite }}
                >
                  {scope}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: isAdmin ? "#fff" : passtrackColors.blue900,
                    background: isAdmin
                      ? passtrackColors.blue
                      : passtrackColors.cloud,
                    borderRadius: 99,
                    px: 1.25,
                    py: 0.4,
                    justifySelf: "start",
                  }}
                >
                  {u.role}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  aria-label={`Eliminar ${u.name || "usuario"}`}
                  onClick={() => onRemoveUser(u.id)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0.5,
                    background: "none",
                    border: "none",
                    color: passtrackColors.red,
                    cursor: "pointer",
                    justifySelf: "center",
                    borderRadius: 1,
                    "&:hover": {
                      background: "rgba(228, 80, 31, 0.08)",
                    },
                  }}
                >
                  <Trash2 size={14} />
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            background: passtrackColors.mist,
            border: `1px solid ${passtrackColors.borderSubtle}`,
            borderRadius: 2.5,
            p: 3,
          }}
        >
          <Typography fontWeight={600} color={passtrackColors.ink}>
            Invitar usuario
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: passtrackColors.graphite,
              }}
            >
              Rol del usuario
              <RequiredMark />
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(3, minmax(0,1fr))",
                },
                gap: 1.5,
              }}
            >
              {INVITE_ROLES.map((r) => {
                const on = newUser.role === r.key;
                const Icon = ROLE_ICONS[r.icon] ?? Shield;
                return (
                  <Box
                    key={r.key}
                    component="button"
                    type="button"
                    onClick={() =>
                      onUpdateNewUser({
                        role: r.key,
                        unit: unitNameForInviteRole(r.key, newUser.unit),
                      })
                    }
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                      textAlign: "left",
                      background: on ? passtrackColors.cloud : "#fff",
                      border: `1.5px solid ${
                        on ? passtrackColors.blue : passtrackColors.borderSubtle
                      }`,
                      borderRadius: 2,
                      px: 1.75,
                      py: 1.75,
                      cursor: "pointer",
                      font: "inherit",
                      transition:
                        "background-color 120ms ease, border-color 120ms ease",
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.15,
                        color: on
                          ? passtrackColors.blue
                          : passtrackColors.textMuted,
                        display: "inline-flex",
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={20} strokeWidth={2} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: passtrackColors.ink,
                          lineHeight: 1.3,
                        }}
                      >
                        {r.label}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.35,
                          fontSize: 12,
                          lineHeight: 1.4,
                          color: passtrackColors.textMuted,
                        }}
                      >
                        {r.hint}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
              },
            }}
          >
            <TextField
              label="Nombre"
              size="small"
              fullWidth
              value={newUser.name}
              onChange={(e) => onUpdateNewUser({ name: e.target.value })}
              placeholder="Ej. Julio Ramos"
              required
            />
            <TextField
              label="Correo"
              type="email"
              size="small"
              fullWidth
              value={newUser.email}
              onChange={(e) => onUpdateNewUser({ email: e.target.value })}
              placeholder="julio@correo.com"
              required
              error={Boolean(emailError) && newUser.email.trim().length > 0}
              helperText={
                newUser.email.trim().length > 0 ? emailError : undefined
              }
            />
            <Box sx={{ display: "flex", gap: 1, gridColumn: { sm: "1 / -1" } }}>
              <FormControl size="small" sx={{ minWidth: 96 }} required>
                <InputLabel id="invite-phone-code-label">Código</InputLabel>
                <Select
                  labelId="invite-phone-code-label"
                  label="Código"
                  value={newUser.phoneCountryCode}
                  onChange={(e) =>
                    onUpdateNewUser({ phoneCountryCode: e.target.value })
                  }
                >
                  {PHONE_COUNTRY_CODES.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Teléfono"
                size="small"
                fullWidth
                placeholder="10 dígitos"
                value={newUser.phoneNumber}
                onChange={(e) =>
                  onUpdateNewUser({
                    phoneNumber: e.target.value.replace(/\D/g, "").slice(0, 10),
                  })
                }
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                required
                error={Boolean(phoneError) && newUser.phoneNumber.length > 0}
                helperText={
                  newUser.phoneNumber.length > 0
                    ? phoneError
                    : "10 dígitos sin espacios."
                }
              />
            </Box>
            {roleMeta.needsUnit && (
              <FormControl
                size="small"
                fullWidth
                required
                sx={{ gridColumn: { sm: "1 / -1" } }}
              >
                <InputLabel id="new-user-unit-label">Unidad</InputLabel>
                <Select
                  labelId="new-user-unit-label"
                  label="Unidad"
                  value={newUser.unit}
                  onChange={(e) => onUpdateNewUser({ unit: e.target.value })}
                >
                  {units
                    .filter((u) => !u.locked)
                    .map((u) => (
                      <MenuItem key={u.id} value={u.name}>
                        {u.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {roleMeta.needsPoints && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: passtrackColors.graphite,
                  }}
                >
                  Puntos de acceso asignados
                  <RequiredMark />
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: passtrackColors.textMuted }}
                >
                  {newUser.points.length
                    ? `${newUser.points.length} de ${accessPoints.length} seleccionados`
                    : "Selecciona al menos uno"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.25,
                }}
              >
                {accessPoints.map((p) => {
                  const on = newUser.points.includes(p.id);
                  const doorsCount = p.gates.length;
                  return (
                    <Box
                      key={p.id}
                      component="button"
                      type="button"
                      onClick={() =>
                        onUpdateNewUser({
                          points: on
                            ? newUser.points.filter((x) => x !== p.id)
                            : [...newUser.points, p.id],
                        })
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        textAlign: "left",
                        background: on ? passtrackColors.cloud : "#fff",
                        border: `1.5px solid ${
                          on
                            ? passtrackColors.blue
                            : passtrackColors.borderSubtle
                        }`,
                        borderRadius: 2,
                        p: 1.5,
                        cursor: "pointer",
                        font: "inherit",
                      }}
                    >
                      <Checkbox
                        checked={on}
                        tabIndex={-1}
                        disableRipple
                        onClick={(e) => e.stopPropagation()}
                        onChange={() =>
                          onUpdateNewUser({
                            points: on
                              ? newUser.points.filter((x) => x !== p.id)
                              : [...newUser.points, p.id],
                          })
                        }
                        sx={{
                          p: 0,
                          color: passtrackColors.slate,
                          "&.Mui-checked": { color: passtrackColors.blue },
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: passtrackColors.ink,
                            lineHeight: 1.3,
                          }}
                        >
                          {p.nombre || "Punto sin nombre"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: passtrackColors.textMuted,
                          }}
                        >
                          {doorsCount}{" "}
                          {doorsCount === 1 ? "puerta" : "puertas"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Typography
                sx={{ fontSize: 12, color: passtrackColors.textMuted }}
              >
                El guardia solo podrá registrar accesos en los puntos
                seleccionados.
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.5,
                color: passtrackColors.textMuted,
              }}
            >
              {newUser.role === "Guardia de Seguridad"
                ? ""
                : "Enviaremos una invitación por correo con acceso temporal."}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={onAddUser}
              disabled={!canAddUser}
              sx={{
                background: canAddUser
                  ? passtrackColors.green
                  : passtrackColors.borderDefault,
                color: "#fff",
                border: "none",
                borderRadius: 1.5,
                px: 2.5,
                py: 1.25,
                fontWeight: 600,
                fontSize: 14,
                cursor: canAddUser ? "pointer" : "not-allowed",
                font: "inherit",
                ml: "auto",
                opacity: canAddUser ? 1 : 0.72,
              }}
            >
              Agregar usuario
            </Box>
          </Box>
        </Box>

        {!hasAdminUser && (
          <Typography sx={{ mt: 2, fontSize: 12, color: passtrackColors.red }}>
            Debes invitar al menos un usuario con rol Administración para
            continuar.
          </Typography>
        )}
      </Card>
    </Box>
  );
}
